// @ts-strict-ignore
import type { UseQueryOptions } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { useContext } from 'react';
import type { UserContextValue } from '~/dashboard/context';
import { UserContext } from '~/dashboard/context';
import log from '~/helpers/logging';
import type { Cells, CustomerData } from '../types';
import { ResourceType } from '../types';
import { fetchAwsToken, getCurrentCell } from './fetchTokens';
import { formatCustomerData, updateCellAppUrlForMultiTLDEnvs } from './formatData';
import type { Agent, CustomerAgentsValue } from '~/application-review/ApplicationReviewTypes';

type QueryReturnType = {
  cells: Cells;
  customers: CustomerData[];
};

type GetCustomersFilterOptions = {
  parentOrganizationSlug?: string;
};

type GetCustomersFilters = {
  filters: GetCustomersFilterOptions | undefined;
};

type Options = Omit<UseQueryOptions<QueryReturnType, unknown>, 'queryKey' | 'queryFn'>;

const getParentOrganizationFilter = (customer: UserContextValue['customer'], customerAgents: CustomerAgentsValue[]) => {
  if (customer.isAfefParentOrganization) {
    return customer.organizationNameSlug;
  }

  const primaryAccounts = customerAgents.filter(
    (customerAgent: CustomerAgentsValue) => customerAgent.isAfefParentOrganization,
  );

  if (primaryAccounts && primaryAccounts.length > 0) {
    return primaryAccounts[0].organizationNameSlug;
  }

  // ideally this should never happen, but should it happen then
  // we want to ensure no extra data is retrieved
  return customer.organizationNameSlug;
};

/**
 * Fetch customer data from the provisioning lambda
 */
export const fetchCustomers = async (
  agent: Agent,
  provisioningLambdaUrl: string,
  alloyEnvironment: string,
  customer: UserContextValue['customer'],
  awsRegion?: string,
) => {
  const getCustomersFilters: GetCustomersFilters = agent.isAfefParentAgent
    ? {
      filters: {
        parentOrganizationSlug: getParentOrganizationFilter(
          customer,
          agent.customerAgents,
        ),
      },
    } : undefined;
  const awsToken = await fetchAwsToken({
    email: agent?.email,
    provisioningLambdaUrl,
    resource: ResourceType.CUSTOMERS,
    isAfefParentAgentRequest: agent.isAfefParentAgent,
    alloyEnvironment,
    payload: getCustomersFilters,
    awsRegion,
  });

  if (!awsToken) {
    log('error', 'Undefined AWS token exception');
    throw new Error('Invalid or Undefined AWS token exception');
  }

  const response = await fetch(provisioningLambdaUrl, {
    method: 'POST',
    headers: awsToken,
    body: JSON.stringify({
      resource: ResourceType.CUSTOMERS,
      email: agent?.email,
      isAfefParentAgentRequest: agent.isAfefParentAgent || undefined,
      requestCellKey: getCurrentCell(alloyEnvironment),
      ...getCustomersFilters,
    }),
  });
  const jsonResp = await response.json();

  if (response.status !== 200) {
    const message = 'Error fetching provisioning customer data exception';
    const error = new Error(message);
    log(
      'error',
      message,
      { status: response.status },
      error,
    );
    throw error;
  }

  return jsonResp;
};
export const useGetCustomers = (options?: Options) => {
  const {
    agent, alloyEnvironment, provisioningLambdaUrl, customer, awsGlobalServicesRegion,
  } = useContext(UserContext);

  const multiTldEnvs = ['platformkube-qa', 'corekube-prod'];

  return useQuery({
    enabled: agent?.email !== undefined,
    queryKey: [provisioningLambdaUrl, ResourceType.CUSTOMERS, { email: agent.email }],
    queryFn: async () => {
      if (!provisioningLambdaUrl) {
        log('error', 'Invalid provisioning URL');
        throw new Error('Invalid or Undefined AWS token exception');
      }

      const preparedResponse = await fetchCustomers(
        agent,
        provisioningLambdaUrl,
        alloyEnvironment,
        customer,
        awsGlobalServicesRegion,
      );
      const customers = preparedResponse.data.customers?.length
        ? preparedResponse.data.customers
        : [];

      const cells = preparedResponse.data.cells ?? {};

      return {
        cells: multiTldEnvs.includes(alloyEnvironment)
          ? updateCellAppUrlForMultiTLDEnvs(cells, alloyEnvironment) : cells,
        customers: formatCustomerData(customers, cells),
      };
    },
    ...options,
  });
};
